import axios from "axios";
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { env } from "../constants/env";
import { toast } from "react-toastify";

export const MarketContext = createContext();

const defaultAssets = [
  {
    symbol: "GOLD.pf",
    price: 100.0,
    open: 99.5,
    high: 101.0,
    low: 98.0,
    close: 100.0,
  },
  {
    symbol: "BTC/USD.pf",
    price: 68000,
    open: 67500,
    high: 68500,
    low: 67000,
    close: 68000,
  },
];

export const MarketProvider = ({ children }) => {
  const [tickers, setTickers] = useState(() => {
    const saved = localStorage.getItem("tickers");
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map((ticker) => ({
        ...ticker,
        open: ticker.open || ticker.price || 0,
        high: ticker.high || ticker.price || 0,
        low: ticker.low || ticker.price || 0,
        close: ticker.close || ticker.price || 0,
      }));
    }
    return defaultAssets;
  });

  const [selected, setSelected] = useState(tickers[0]);

  // ---------- POSITIONS (open trades)
  const [positions, setPositions] = useState(() => {
    const saved = localStorage.getItem("positions");
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    const now = Date.now();
    return parsed.map((p) => {
      const remaining = Math.max(0, Math.floor((p.expiry - now) / 1000));
      return { ...p, remaining };
    });
  });

  // ---------- CLOSED POSITIONS
  const [closedPositions, setClosedPositions] = useState(() => {
    const saved = localStorage.getItem("closedPositions");
    return saved ? JSON.parse(saved) : [];
  });

  const [balance, setBalance] = useState(0);
  const [banner, setBanner] = useState(null);
  const [inactive, setInactive] = useState(false);

  const tickersRef = useRef(tickers);
  const balanceRef = useRef(balance);
  const closedPositionsRef = useRef(closedPositions);
  const token = localStorage.getItem("userToken");

  // 🧹 One-time cleanup: remove trades older than 6 minutes
  useEffect(() => {
    const now = Date.now();

    setPositions((prev) =>
      prev.filter((p) => now - p.time * 1000 < 6 * 60 * 1000),
    );
  }, []);

  // --------------------------------------
  // Load arena data from backend
  // --------------------------------------
  useEffect(() => {
    const fetchArenaData = async () => {
      try {
        const response = await axios.get(`${env.apiUrl}/api/user/arena`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data;
        setBalance(data.balance);
        setClosedPositions(data.closedPositions || []);
      } catch (err) {
        console.warn("⚠️ Failed to load arena data:", err);
      }
    };

    fetchArenaData();
  }, []);

  // --------------------------------------
  // Fetch initial tickers from backend
  // --------------------------------------
  useEffect(() => {
    const fetchInitialTickers = async () => {
      try {
        const res = await axios.get(`${env.apiUrl}/api/tickers`);
        if (res.data.length > 0) {
          const formattedTickers = res.data.map((ticker) => ({
            symbol: ticker.symbol,
            price: ticker.price || ticker.close || 0,
            open: ticker.open || ticker.price || 0,
            high: ticker.high || ticker.price || 0,
            low: ticker.low || ticker.price || 0,
            close: ticker.close || ticker.price || 0,
          }));

          setTickers(formattedTickers);
          setSelected(formattedTickers[0]);
        }
      } catch (err) {
        console.warn("⚠️ Failed to fetch initial tickers:", err);
      }
    };

    fetchInitialTickers();
  }, []);

  // --------------------------------------
  // LIVE DATA FETCH from your API (every second)
  // --------------------------------------
  useEffect(() => {
    if (!selected?.symbol) return;

    const fetchLiveData = async () => {
      if (inactive) return;

      try {
        // convert / to - before using in URL
        const safeSymbol = selected.symbol.replace("/", "-");
        const res = await axios.get(
          `${env.apiUrl}/api/history/${safeSymbol}/latest`,
        );

        if (res.data) {
          const newData = res.data;

          setTickers((prev) => {
            const updated = prev.map((ticker) => {
              if (ticker.symbol === selected.symbol) {
                // Get current time for candle calculations
                const now = Math.floor(Date.now() / 1000);
                const candleInterval = 30; // seconds per candle
                const currentCandleStart =
                  Math.floor(now / candleInterval) * candleInterval;

                // If we don't have candle start time or it's a new candle period
                if (
                  !ticker.candleStartTime ||
                  currentCandleStart > ticker.candleStartTime
                ) {
                  // New candle: reset OHLC to current price
                  const currentPrice =
                    newData.value || newData.close || ticker.price;
                  return {
                    ...ticker,
                    price: currentPrice,
                    open: currentPrice,
                    high: currentPrice,
                    low: currentPrice,
                    close: currentPrice,
                    candleStartTime: currentCandleStart,
                  };
                } else {
                  // Same candle: update OHLC
                  const currentPrice =
                    newData.value || newData.close || ticker.price;
                  return {
                    ...ticker,
                    price: currentPrice,
                    high: Math.max(ticker.high, currentPrice),
                    low: Math.min(ticker.low, currentPrice),
                    close: currentPrice,
                  };
                }
              }
              return ticker;
            });

            // Update tickers in backend
            pushTickersUpdate(updated);
            return updated;
          });
        }
      } catch (err) {
        console.warn("⚠️ Failed to fetch live data:", err);
      }
    };

    // Fetch immediately
    fetchLiveData();

    // Set up interval for live updates (every 1 second for real-time feel)
    const liveInterval = setInterval(fetchLiveData, 1000);

    return () => clearInterval(liveInterval);
  }, [selected, inactive]);

  // Also update all tickers periodically (every 5 seconds)
  useEffect(() => {
    const fetchAllTickers = async () => {
      if (inactive) return;

      try {
        const promises = tickers.map(async (ticker) => {
          try {
            const safeSymbol = ticker.symbol.replace("/", "-");
            const res = await axios.get(
              `${env.apiUrl}/api/history/${safeSymbol}/latest`,
            );

            if (res.data) {
              const newData = res.data;
              const now = Math.floor(Date.now() / 1000);
              const candleInterval = 30;
              const currentCandleStart =
                Math.floor(now / candleInterval) * candleInterval;

              // Check if we need to start a new candle
              if (
                !ticker.candleStartTime ||
                currentCandleStart > ticker.candleStartTime
              ) {
                const currentPrice =
                  newData.value || newData.close || ticker.price;
                return {
                  ...ticker,
                  price: currentPrice,
                  open: currentPrice,
                  high: currentPrice,
                  low: currentPrice,
                  close: currentPrice,
                  candleStartTime: currentCandleStart,
                };
              } else {
                const currentPrice =
                  newData.value || newData.close || ticker.price;
                return {
                  ...ticker,
                  price: currentPrice,
                  high: Math.max(ticker.high, currentPrice),
                  low: Math.min(ticker.low, currentPrice),
                  close: currentPrice,
                };
              }
            }
          } catch (err) {
            console.warn(`Failed to update ${ticker.symbol}:`, err);
            return ticker;
          }
          return ticker;
        });

        const updatedTickers = await Promise.all(promises);
        setTickers(updatedTickers);
        pushTickersUpdate(updatedTickers);
      } catch (err) {
        console.warn("⚠️ Failed to fetch all tickers:", err);
      }
    };

    // Update all tickers every 5 seconds
    const allTickersInterval = setInterval(fetchAllTickers, 5000);

    return () => clearInterval(allTickersInterval);
  }, [tickers, inactive]);

  // Persist tickers
  useEffect(() => {
    tickersRef.current = tickers;
    localStorage.setItem("tickers", JSON.stringify(tickers));
  }, [tickers]);

  // Keep balance and closedPositions refs in sync
  useEffect(() => {
    balanceRef.current = balance;
  }, [balance]);

  useEffect(() => {
    closedPositionsRef.current = closedPositions;
  }, [closedPositions]);

  // Persist closed positions
  useEffect(() => {
    localStorage.setItem("closedPositions", JSON.stringify(closedPositions));
  }, [closedPositions]);

  // Persist open positions
  useEffect(() => {
    localStorage.setItem("positions", JSON.stringify(positions));
  }, [positions]);

  // --------------------------------------
  // CLOSE POSITION (using tradeId)
  // --------------------------------------
  const closePosition = useCallback((tradeId) => {
    setPositions((prevPositions) => {
      const pos = prevPositions.find((p) => p.tradeId === tradeId);
      if (!pos) return prevPositions;

      // 🔒 Lock expiry price once
      const liveTicker = tickersRef.current.find(
        (t) => t.symbol === pos.symbol,
      );

      const currentPrice = pos.expiryPrice ?? liveTicker?.price ?? pos.entry;

      const pnl =
        pos.type === "buy"
          ? currentPrice - pos.entry
          : pos.entry - currentPrice;

      const win = pnl > 0;
      const totalPayout = win ? pos.payout + pos.amount : 0;

      const closedTrade = {
        ...pos,
        win,
        finalPrice: currentPrice,
        pnl,
      };

      // Compute new values for backend sync from current refs
      const newBalance = balanceRef.current + totalPayout;
      const alreadyClosed = closedPositionsRef.current.some(
        (p) => p.tradeId === pos.tradeId,
      );
      const newClosedPositions = alreadyClosed
        ? closedPositionsRef.current
        : [...closedPositionsRef.current, closedTrade];

      // Update React state
      setBalance((prevBalance) => prevBalance + totalPayout);
      setClosedPositions(newClosedPositions);

      // Sync computed values to backend
      syncArenaData(newBalance, newClosedPositions);

      if (win) {
        setBanner({
          type: "win",
          payout: pos.payout + pos.amount,
          profit: pos.payout,
          symbol: pos.symbol,
        });
      } else {
        setBanner({
          type: "loss",
          text: `You lost ${pos.amount.toFixed(2)}.`,
          symbol: pos.symbol,
        });
      }

      setTimeout(() => setBanner(null), 3000);

      return prevPositions.filter((p) => p.tradeId !== tradeId);
    });
  }, []);

  // --------------------------------------
  // COUNTDOWN TIMER
  // --------------------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      setPositions((prev) => {
        let toClose = [];

        const updated = prev.map((p) => {
          if (p.remaining > 0) {
            const newRemaining = p.remaining - 1;
            if (newRemaining <= 0) {
              toClose.push(p.tradeId);
              return { ...p, remaining: 0 };
            }
            return { ...p, remaining: newRemaining };
          }
          return p;
        });

        toClose.forEach((tradeId) => {
          setTimeout(() => closePosition(tradeId), 0);
        });

        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [closePosition]);

  // --------------------------------------
  // PLACE ORDER
  // --------------------------------------
  const placeOrder = (
    tradeId,
    type,
    amount,
    duration,
    entryPrice,
    payoutPercent,
  ) => {
    const payout = Math.round(amount * (payoutPercent / 100));

    const newPos = {
      tradeId,
      symbol: selected.symbol,
      type,
      entry: entryPrice,
      amount,
      payout,
      expiry: Date.now() + duration * 1000,
      remaining: duration,
      time: Math.floor(Date.now() / 1000),
      expiryPrice: null,
    };

    setPositions((prev) => [...prev, newPos]);
    setBalance((b) => b - amount);
  };

  // --------------------------------------
  // Sync arena with backend
  // --------------------------------------
  const syncArenaData = async (updatedBalance, updatedClosedPositions) => {
    try {
      const res = await axios.put(
        `${env.apiUrl}/api/user/arena`,
        {
          balance: updatedBalance,
          closedPositions: updatedClosedPositions,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = res.data.arena;

      if (data.balance === 250 && updatedBalance <= 0) {
        toast.success("$PROP replenished!");
      }

      setBalance(data.balance);
    } catch (err) {
      console.warn("⚠️ Failed to sync arena data:", err);
    }
  };

  const pushTickersUpdate = async (updatedTickers) => {
    try {
      await axios.put(`${env.apiUrl}/api/tickers`, {
        tickers: updatedTickers,
      });
    } catch (err) {
      console.warn("⚠️ Failed to push tickers update:", err);
    }
  };

  return (
    <MarketContext.Provider
      value={{
        tickers,
        selected,
        setSelected,
        positions,
        closedPositions,
        setClosedPositions,
        placeOrder,
        closePosition,
        balance,
        setBalance,
        banner,
        inactive,
        setInactive,
      }}
    >
      {children}
    </MarketContext.Provider>
  );
};
